import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckRounded } from "@mui/icons-material";
import dayjs from "dayjs";
import {
  BASIC_SCREENER_SESSIONS,
  BillingInterval,
  PRICING_TIERS,
  PricingTier,
  formatPrice,
  formatPriceWhole,
  getTierByVariantId,
  isLifetimeVariant,
} from "../../data/pricingPlans";
import { useChangePlan } from "../../hooks/useMembershipApi";
import { useChatQuota } from "../Chat/hooks/useChatQuota";
import { MembershipStatus } from "../../types/lemonSqueezy";
import { isNative } from "../../lib/platform";
import { PlanDialogShell } from "./PlanDialogShell";
import { TopUpCreditsDialog } from "../Membership/TopUpCredits";

// Shared primary CTA for the billing dialogs (flat: solid colour, no shadows).
const dialogPrimaryActionSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.9375rem",
  borderRadius: 2,
  py: 1.25,
  backgroundColor: "#22c55e",
  color: "#ffffff",
  "&:hover": { backgroundColor: "#16a34a" },
} as const;

interface PlanSummaryRow {
  label: string;
  value: string;
  emphasize?: boolean;
}

/**
 * Receipt-style summary: uppercase caption labels against a subtle surface
 * tint, values right-aligned, the money row emphasised in brand green.
 */
const PlanSummaryList = ({ rows }: { rows: PlanSummaryRow[] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"}`,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
      }}
    >
      {rows.map((row, index) => (
        <Box
          key={row.label}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 1.5,
            borderTop:
              index === 0
                ? "none"
                : `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)"}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: theme.palette.text.secondary,
              flexShrink: 0,
            }}
          >
            {row.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: row.emphasize ? 700 : 600,
              color: row.emphasize ? "#22c55e" : theme.palette.text.primary,
              textAlign: "right",
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/** "£9.99/mo" or "£99/yr": a plan's price on the member's own billing term. */
const formatTierPrice = (tier: PricingTier, interval: BillingInterval): string =>
  interval === "monthly"
    ? `${formatPrice(tier.monthly)}/mo`
    : `${formatPriceWhole(tier.yearly)}/yr`;

interface ChangePlanDialogProps {
  membership: NonNullable<MembershipStatus["membership"]>;
  /** The plan to switch to; null keeps the dialog closed. */
  target: PricingTier["key"] | null;
  onClose: () => void;
  onChanged: (target: PricingTier["key"]) => void;
}

/**
 * The confirmed plan switch (POST /membership/change-plan, immediate and
 * prorated server-side on the member's current billing term): a receipt of
 * what changes and what money moves today, then one confirm. Shared by
 * account settings and the upgrade page. Lemon Squeezy subscriptions only.
 * It charges money, so it renders nothing in the native app (App Store
 * 3.1.1); isNative() is constant, so branching before hooks is safe.
 */
export const ChangePlanDialog = (props: ChangePlanDialogProps) => {
  if (isNative()) return null;
  return <WebChangePlanDialog {...props} />;
};

const WebChangePlanDialog = ({
  membership,
  target,
  onClose,
  onChanged,
}: ChangePlanDialogProps) => {
  const theme = useTheme();
  const changePlanMutation = useChangePlan();
  const current = getTierByVariantId(membership.variantId);
  const interval = current?.interval ?? "monthly";
  const tierPrice = (tier: PricingTier): string => formatTierPrice(tier, interval);

  const confirmingTier = PRICING_TIERS.find((tier) => tier.key === target);
  const isUpgrade = target === "pro";
  const isTrialing = membership.status === "on_trial";

  /**
   * Client-side estimate of the prorated money movement — Lemon Squeezy has
   * no proration-preview API, so this mirrors its arithmetic and shows every
   * figure with a "~". The invoice LS raises on confirm is authoritative (it
   * also nets any rolling account credit from earlier plan changes, which
   * this estimate can't see). A tier change during a trial preserves the
   * existing trial and therefore charges nothing today.
   */
  const estimateProration = (
    targetTier: PricingTier,
  ): { due: number; accountCredit: number } | null => {
    if (!current || !membership.renewsAt) return null;
    // A stale renewal date (missed webhooks leaving renewsAt in the past)
    // would zero the fraction and show a misleading ~£0.00 — decline to
    // guess; LS computes the real charge from its own, current cycle.
    if (dayjs(membership.renewsAt).isBefore(dayjs())) return null;
    const price = (tier: PricingTier) =>
      interval === "monthly" ? tier.monthly : tier.yearly;
    if (isTrialing) {
      return { due: 0, accountCredit: 0 };
    }
    const cycleDays = interval === "monthly" ? 30 : 365;
    const fractionRemaining = Math.min(
      1,
      Math.max(0, dayjs(membership.renewsAt).diff(dayjs(), "day") / cycleDays),
    );
    // Same interval on both sides, so the cycle continues: the new price
    // covers only the remainder of it, credited with the unused old price.
    const charge = price(targetTier) * fractionRemaining;
    const credit = price(current.tier) * fractionRemaining;
    return {
      due: Math.max(0, charge - credit),
      accountCredit: Math.max(0, credit - charge),
    };
  };

  // Receipt rows for the confirm dialog: what changes, what money moves
  // today, and what happens at renewal — visible BEFORE the click that bills.
  const summaryRows: PlanSummaryRow[] = [];
  if (confirmingTier && current) {
    summaryRows.push({
      label: "Current plan",
      value: `${current.tier.name} · ${tierPrice(current.tier)}`,
    });
    summaryRows.push({
      label: "New plan",
      value: `${confirmingTier.name} · ${tierPrice(confirmingTier)}`,
    });
    const proration = estimateProration(confirmingTier);
    let dueValue = "Prorated at billing";
    if (proration) {
      dueValue = isTrialing
        ? formatPrice(proration.due)
        : `~${formatPrice(proration.due)}`;
    }
    summaryRows.push({ label: "Due today", value: dueValue, emphasize: true });
    if (proration && proration.accountCredit >= 0.01) {
      summaryRows.push({
        label: "Account credit",
        value: `~${formatPrice(proration.accountCredit)}`,
      });
    }
    // The existing renewal/trial-end date is unchanged and bills the new
    // plan's price when it arrives.
    const renewalDate =
      membership.renewsAt && dayjs(membership.renewsAt).isAfter(dayjs())
        ? dayjs(membership.renewsAt)
        : null;
    if (renewalDate) {
      summaryRows.push({
        // A trialist's first payment is the new plan's full price, at the
        // trial's end.
        label: isTrialing ? "First payment" : "Then renews",
        value: `${renewalDate.format("MMM D, YYYY")} · ${tierPrice(confirmingTier)}`,
      });
    }
  }

  const closeConfirm = () => {
    onClose();
    changePlanMutation.reset();
  };

  const handleConfirmChange = async () => {
    if (!target) return;
    try {
      await changePlanMutation.mutateAsync({ target, interval });
      onChanged(target);
    } catch {
      // Error surfaced via changePlanMutation.error inside the dialog.
    }
  };

  return (
    <PlanDialogShell
      open={target !== null}
      onClose={closeConfirm}
      title={isUpgrade ? "Upgrade to Pro" : "Downgrade to Basic"}
      subtitle={
        isUpgrade
          ? "Unlimited screening, weekly AI chat, 4 reports a week and credit top-ups"
          : `${BASIC_SCREENER_SESSIONS} screener sessions a month, without the weekly AI`
      }
    >
      <PlanSummaryList rows={summaryRows} />

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: theme.palette.text.secondary,
          mt: 1.5,
          mb: 2.5,
          lineHeight: 1.5,
        }}
      >
        {isUpgrade &&
          isTrialing &&
          "Your free trial continues on Pro. Weekly AI unlocks immediately, and billing starts when your current trial ends."}
        {isUpgrade &&
          !isTrialing &&
          "Estimated — the unused time on your Basic plan and any credit on your account are applied against today's charge. The Lemon Squeezy invoice is final."}
        {!isUpgrade &&
          `Weekly AI chat and reports stop straight away, screening drops to ${BASIC_SCREENER_SESSIONS} sessions a month and saved screens become read-only. Purchased credits pause until you're back on Pro. Unused Pro time becomes credit toward future invoices.`}
      </Typography>

      {changePlanMutation.error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {changePlanMutation.error.message}
        </Alert>
      )}

      <Button
        onClick={handleConfirmChange}
        disabled={changePlanMutation.isPending}
        fullWidth
        disableElevation
        sx={dialogPrimaryActionSx}
      >
        {changePlanMutation.isPending && (
          <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
        )}
        {!changePlanMutation.isPending && (isUpgrade ? "Confirm upgrade" : "Confirm downgrade")}
      </Button>
      <Button
        onClick={closeConfirm}
        fullWidth
        sx={{
          mt: 1,
          textTransform: "none",
          fontWeight: 500,
          color: theme.palette.text.secondary,
        }}
      >
        {isUpgrade ? "Keep Basic" : "Keep Pro"}
      </Button>
    </PlanDialogShell>
  );
};

interface ManagePlanProps {
  membership: NonNullable<MembershipStatus["membership"]>;
}

/**
 * Plan panel in account settings (web): BOTH plans always render as tiles,
 * the current one badged, the other actionable — upgrade and downgrade are
 * each one confirmed click (POST /membership/change-plan, immediate and
 * prorated server-side). Pro's tile carries the credit balance and top-up.
 */
/**
 * Defense-in-depth wrapper: the inner component charges money through Lemon
 * Squeezy and must NEVER render in the native app (App Store 3.1.1). The
 * AccountSettings call site already gates it; this guard survives any future
 * refactor. isNative() is constant for the app's lifetime, so branching
 * before hooks is safe.
 */
export const ManagePlan = (props: ManagePlanProps) => {
  if (isNative()) return null;
  return <WebManagePlan {...props} />;
};

const WebManagePlan = ({ membership }: ManagePlanProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data: quota } = useChatQuota();

  const current = getTierByVariantId(membership.variantId);
  // Unknown/legacy variants are Pro everywhere else in the system (the
  // fail-safe that grandfathers old subscribers) — mirror that here.
  const currentKey = current?.tier.key ?? "pro";
  const isLifetime = isLifetimeVariant(membership.variantId);

  const [confirmingTarget, setConfirmingTarget] = useState<PricingTier["key"] | null>(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const cardSx = {
    backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(255, 255, 255, 0.9)",
    border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
    borderRadius: 3,
    p: 3,
  };

  // Lifetime members have no renewals, no plan changes, and no top-up
  // purchase surface (their extra usage is support-granted).
  if (isLifetime) {
    return (
      <Box sx={{ ...cardSx, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Your Plan
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Lifetime plan — yours forever. No renewals, no plan changes needed.
        </Typography>
      </Box>
    );
  }

  const interval = current?.interval ?? "monthly";
  const tierPrice = (tier: PricingTier): string => formatTierPrice(tier, interval);
  const balanceCredits = quota?.credits?.balanceCredits ?? 0;
  const canTopUp = quota?.credits?.canTopUp === true;
  const weeklyExceeded = quota?.exceeded === true;
  const weeklyPercentUsed = quota?.percentUsed ?? 0;
  // Purchased credits from a former Pro plan freeze on Basic — never shown as
  // gone, or a downgrade reads as confiscation.
  const frozenCredits = quota?.credits?.frozenCredits ?? 0;

  return (
    <Box sx={{ ...cardSx, mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Your Plan
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 2.5 }}>
        {membership.renewsAt && dayjs(membership.renewsAt).isAfter(dayjs())
          ? `Renews ${dayjs(membership.renewsAt).format("D MMMM YYYY")} · billed ${
              interval === "monthly" ? "monthly" : "annually"
            }`
          : "Active"}
      </Typography>

      {/* Both plans, always: the current one is badged, the other is one
          confirmed click away — no hunting for where the other plan went. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {PRICING_TIERS.map((tier) => {
          const isCurrent = tier.key === currentKey;

          return (
            <Box
              key={tier.key}
              sx={{
                display: "flex",
                flexDirection: "column",
                p: 2.5,
                borderRadius: 2.5,
                border: isCurrent
                  ? "1px solid #22c55e"
                  : `1px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`,
                backgroundColor: isCurrent
                  ? "rgba(34, 197, 94, 0.04)"
                  : isDark
                    ? "rgba(255, 255, 255, 0.02)"
                    : "rgba(0, 0, 0, 0.02)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{tier.name}</Typography>
                {isCurrent && (
                  <Chip
                    label="Current plan"
                    size="small"
                    sx={{
                      height: 20,
                      fontWeight: 600,
                      fontSize: "0.66rem",
                      backgroundColor: "rgba(34, 197, 94, 0.12)",
                      color: isDark ? "#22c55e" : "#16a34a",
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {tierPrice(tier).split("/")[0]}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  /{tierPrice(tier).split("/")[1]}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 2 }}>
                {tier.features.map((point) => (
                  <Box key={point} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <CheckRounded sx={{ fontSize: 15, mt: "2px", color: "#22c55e" }} />
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.secondary, fontSize: "0.8rem" }}
                    >
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Footer pinned to the tile base so the two tiles' actions align. */}
              <Box sx={{ mt: "auto", pt: 2 }}>
                {isCurrent && tier.key === "pro" && (
                  <>
                    {/* Both usage buckets, side by side — labelled cells so a
                        fresh upgrader's "0 credits" reads as an empty reserve
                        next to a full weekly allowance, never as "no usage
                        left". Same surface language as the receipt rows. */}
                    <Box
                      sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
                    >
                      {[
                        {
                          label: "Weekly AI Usage",
                          value: `${100 - weeklyPercentUsed}% left`,
                          sub: "Resets every Sunday",
                        },
                        {
                          label: "AI Credits",
                          value: String(balanceCredits),
                          // Top-ups are locked until the first real payment
                          // (trialing subs can't put money through) — say so
                          // rather than silently hiding the button.
                          sub:
                            balanceCredits > 0
                              ? "Spent after weekly AI"
                              : canTopUp
                                ? "Top up any time"
                                : "Unlocks after your first payment",
                        },
                      ].map((stat) => (
                        <Box
                          key={stat.label}
                          sx={{
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 2,
                            border: `1px solid ${
                              isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
                            }`,
                            backgroundColor: isDark
                              ? "rgba(255, 255, 255, 0.02)"
                              : "rgba(0, 0, 0, 0.02)",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.625rem",
                              fontWeight: 600,
                              letterSpacing: "0.5px",
                              textTransform: "uppercase",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.95rem", mt: 0.25 }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: "block",
                              mt: 0.25,
                              fontSize: "0.68rem",
                            }}
                          >
                            {stat.sub}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {canTopUp && (
                      <Button
                        onClick={() => setTopUpOpen(true)}
                        disableElevation
                        fullWidth
                        sx={{ ...dialogPrimaryActionSx, py: 1, mt: 1.5 }}
                      >
                        Top up credits
                      </Button>
                    )}
                  </>
                )}
                {/* Same stat-cell language as the Pro tile, greyed out — the
                    paused reserve reads as a real balance waiting, not prose. */}
                {isCurrent && tier.key === "basic" && frozenCredits > 0 && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 2,
                      opacity: 0.55,
                      border: `1px solid ${
                        isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
                      }`,
                      backgroundColor: isDark
                        ? "rgba(255, 255, 255, 0.02)"
                        : "rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.625rem",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        textTransform: "uppercase",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      AI Credits
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", mt: 0.25 }}>
                      {frozenCredits}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        display: "block",
                        mt: 0.25,
                        fontSize: "0.68rem",
                      }}
                    >
                      Paused — ready when you're back on Pro
                    </Typography>
                  </Box>
                )}
                {!isCurrent && tier.key === "pro" && (
                  <Button
                    onClick={() => setConfirmingTarget("pro")}
                    disableElevation
                    fullWidth
                    sx={{ ...dialogPrimaryActionSx, py: 1 }}
                  >
                    Upgrade to Pro — {tierPrice(tier)}
                  </Button>
                )}
                {!isCurrent && tier.key === "basic" && (
                  <Button
                    onClick={() => setConfirmingTarget("basic")}
                    fullWidth
                    variant="outlined"
                    sx={{
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
                      "&:hover": {
                        borderColor: isDark ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.35)",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Downgrade to Basic
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <ChangePlanDialog
        membership={membership}
        target={confirmingTarget}
        onClose={() => setConfirmingTarget(null)}
        onChanged={(target) => {
          setSuccessMessage(
            target === "pro"
              ? "You're now on Pro. Unlimited screening, weekly AI chat and reports are active."
              : "You're now on Basic. Your unused Pro time is credited toward future invoices.",
          );
          setConfirmingTarget(null);
        }}
      />

      <TopUpCreditsDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
